<template>
  <div
    class="sc-audio"
    :class="{ 'sc-audio--playing': isPlaying, 'sc-audio--flush': flush }"
    draggable="false"
    @dragstart.stop.prevent
    @mousedown.stop
    @pointerdown.stop
  >
    <button
      type="button"
      class="sc-audio__play"
      :aria-label="isPlaying ? 'Pausar áudio' : 'Reproduzir áudio'"
      @click="toggle"
    >
      <v-icon size="22" color="text_primary">
        {{ isPlaying ? "mdi-pause" : "mdi-play" }}
      </v-icon>
    </button>

    <span class="sc-audio__time sc-audio__time--current">{{ formatTime(currentTime) }}</span>

    <input
      ref="scrubber"
      class="sc-audio__scrubber"
      :class="{ 'is-loading': !isReady }"
      type="range"
      min="0"
      :max="duration || 0"
      step="0.01"
      :value="currentTime"
      :disabled="!isReady"
      :style="scrubberStyle"
      aria-label="Posição do áudio"
      @input="onScrub"
      @mousedown="suppressAncestorDrag"
      @touchstart="suppressAncestorDrag"
    />

    <span class="sc-audio__time sc-audio__time--total">{{ formatTime(duration) }}</span>

    <button
      type="button"
      class="sc-audio__mute"
      :aria-label="isMuted ? 'Ativar som' : 'Silenciar'"
      @click="toggleMute"
    >
      <v-icon size="20" color="text_secondary">
        {{ isMuted ? "mdi-volume-off" : "mdi-volume-high" }}
      </v-icon>
    </button>

    <audio
      ref="audio"
      :src="src"
      preload="metadata"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @play="isPlaying = true"
      @pause="isPlaying = false"
      @ended="onEnded"
      @volumechange="isMuted = $refs.audio.muted"
    ></audio>
  </div>
</template>

<script>
export default {
  name: "CardAudioPlayer",
  props: {
    src: { type: String, required: true },
    flush: { type: Boolean, default: false },
  },
  data() {
    return {
      isPlaying: false,
      isReady: false,
      isMuted: false,
      currentTime: 0,
      duration: 0,
    };
  },
  computed: {
    progressPct() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    scrubberStyle() {
      return { "--sc-audio-progress": `${this.progressPct}%` };
    },
  },
  methods: {
    toggle() {
      const a = this.$refs.audio;
      if (!a) return;
      if (a.paused) a.play();
      else a.pause();
    },
    toggleMute() {
      const a = this.$refs.audio;
      if (!a) return;
      a.muted = !a.muted;
    },
    onLoadedMetadata() {
      const a = this.$refs.audio;
      const d = a && a.duration;
      this.duration = Number.isFinite(d) ? d : 0;
      this.isReady = this.duration > 0;
    },
    onTimeUpdate() {
      this.currentTime = this.$refs.audio.currentTime;
    },
    onScrub(event) {
      const value = parseFloat(event.target.value);
      const a = this.$refs.audio;
      if (!a || !Number.isFinite(value)) return;
      a.currentTime = value;
      this.currentTime = value;
    },
    suppressAncestorDrag() {
      const ancestor = this.$el.closest('[draggable="true"]');
      if (!ancestor || this._suppressed) return;
      this._suppressed = ancestor;
      ancestor.setAttribute("draggable", "false");
      const restore = () => {
        ancestor.setAttribute("draggable", "true");
        this._suppressed = null;
        window.removeEventListener("mouseup", restore, true);
        window.removeEventListener("touchend", restore, true);
        window.removeEventListener("touchcancel", restore, true);
      };
      window.addEventListener("mouseup", restore, true);
      window.addEventListener("touchend", restore, true);
      window.addEventListener("touchcancel", restore, true);
    },
    onEnded() {
      this.isPlaying = false;
      this.currentTime = 0;
      if (this.$refs.audio) this.$refs.audio.currentTime = 0;
    },
    formatTime(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
      const total = Math.floor(seconds);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${s.toString().padStart(2, "0")}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.sc-audio {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 14px;
  border-radius: var(--sc-radius-pill);
  background: var(--sc-surface-tint);
  border: 1px solid var(--sc-border-subtle);
  transition: border-color var(--sc-duration-fast) var(--sc-easing-standard),
              background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    border-color: var(--sc-border-medium);
    background: var(--sc-surface-tint-hover);
  }

  &--playing {
    border-color: rgba(var(--view-accent-rgb), 0.4);
    background: rgba(var(--view-accent-rgb), 0.06);
  }

  &--flush {
    margin-top: 8px;
    margin-left: -14px;
  }
}

.sc-audio__play {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--view-accent);
  border: none;
  cursor: pointer;
  transition: transform var(--sc-duration-fast) var(--sc-easing-standard),
              filter var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: var(--view-accent-on) !important;
  }

  &:hover {
    filter: brightness(1.08);
    transform: scale(1.04);
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid var(--view-accent);
    outline-offset: 2px;
  }
}

.sc-audio__scrubber {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  min-width: 0;
  height: 4px;
  background: linear-gradient(
    to right,
    var(--view-accent) 0%,
    var(--view-accent) var(--sc-audio-progress, 0%),
    var(--sc-surface-tint-stronger) var(--sc-audio-progress, 0%),
    var(--sc-surface-tint-stronger) 100%
  );
  border-radius: var(--sc-radius-pill);
  outline: none;
  cursor: pointer;
  transition: height var(--sc-duration-fast) var(--sc-easing-standard);

  &.is-loading {
    cursor: progress;
    opacity: 0.6;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--view-accent);
    border: 2px solid var(--sc-color-bg);
    cursor: pointer;
    transition: transform var(--sc-duration-fast) var(--sc-easing-standard);
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--view-accent);
    border: 2px solid var(--sc-color-bg);
    cursor: pointer;
  }

  &:hover::-webkit-slider-thumb,
  &:focus-visible::-webkit-slider-thumb {
    transform: scale(1.2);
  }
}

.sc-audio__time {
  font-size: var(--sc-text-sm);
  font-variant-numeric: tabular-nums;
  color: var(--v-text_secondary-base);
  line-height: 1;
  flex-shrink: 0;
  min-width: 32px;

  &--current { text-align: right; color: var(--v-text_primary-base); }
  &--total   { text-align: left; }
}

.sc-audio__mute {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--view-accent);
    outline-offset: 2px;
  }
}
</style>
